import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const fireClasses = [
  {
    id: "class-a",
    letter: "A",
    colorClass: "bg-primary-100 text-primary-700 border-primary-200",
    badgeClass: "bg-primary-500 text-background-50",
    title: "Class A — Ordinary Combustibles",
    fuelSources: "Wood, plastics, fabrics, paper, garbage",
    commonLocations: "Homes, offices, businesses and industrial environments",
    description:
      "Class A fires are referred to as ordinary combustibles, often caused by common flammable materials found in homes, offices, businesses and industrial environments. These fires result from materials like wood, plastics, fabrics, paper and garbage. Class A fires are a common occurrence in many residential, commercial and industrial premises today.",
    extinguishing: "Water or a solution of monoammonium phosphate",
    icon: "ri-leaf-line",
  },
  {
    id: "class-b",
    letter: "B",
    colorClass: "bg-accent-100 text-accent-700 border-accent-200",
    badgeClass: "bg-accent-500 text-background-50",
    title: "Class B — Flammable Liquids & Gases",
    fuelSources: "Gasoline, kerosene, paints, oils, propane, butane",
    commonLocations: "Industrial establishments, petroleum processing and storage",
    description:
      "Class B fires are mainly caused by flammable liquids or gas. The most popular fuel sources include gasoline, kerosene, paints, oils and other petroleum-based products. Class B fires also result from flammable gases like propane and butane. These fires are mainly common in industrial establishments that deal with the processing, distribution and storage of petroleum products and gas.",
    extinguishing: "Suppression agent that smothers flames to deprive them of oxygen and stop chemical reactions",
    icon: "ri-drop-line",
  },
  {
    id: "class-c",
    letter: "C",
    colorClass: "bg-secondary-100 text-secondary-700 border-secondary-200",
    badgeClass: "bg-secondary-500 text-background-50",
    title: "Class C — Electrical Components",
    fuelSources: "Transformers, appliances, motors, wiring, electrical equipment",
    commonLocations: "Industrial premises, homes with faulty appliances, small business spaces",
    description:
      "The main fuel source for class C fires is electrical components. The fires often result from faults like short circuits or shocks on electrical systems and equipment. The main areas of electrical faults that fuel class C fires include transformers, appliances and motors. These types of fires are common in industrial premises with heavy electrical equipment that use a lot of power. However, they can also occur in homes and small business spaces as a result of wiring problems or faulty electrical appliances.",
    extinguishing: "Immediate power shutdown and use of non-conductive chemical solutions",
    icon: "ri-flashlight-line",
  },
  {
    id: "class-d",
    letter: "D",
    colorClass: "bg-background-200 text-foreground-700 border-background-300",
    badgeClass: "bg-foreground-700 text-background-50",
    title: "Class D — Combustible Metals",
    fuelSources: "Magnesium, titanium, potassium, aluminium and other combustible metals",
    commonLocations: "Laboratories and industrial manufacturing facilities",
    description:
      "Class D fires are fires that use combustible metals as their primary fuel source. Some of the metals that cause these fires include magnesium, titanium, potassium and aluminium. Class D fires are mostly common in laboratories. However, combustible metals are also used in various industrial manufacturing and production procedures. Such fires are hazardous and cannot be put out using ordinary agents like water.",
    extinguishing: "Powder agents that absorb the heat and smothers the flames",
    icon: "ri-settings-3-line",
  },
  {
    id: "class-k",
    letter: "K",
    colorClass: "bg-primary-50 text-primary-600 border-primary-100",
    badgeClass: "bg-primary-400 text-background-50",
    title: "Class K — Cooking Oils & Fats",
    fuelSources: "Cooking oils, vegetable fats, greases, animal fats",
    commonLocations: "Restaurants, hotels, home kitchens and food preparation areas",
    description:
      "Most people often confuse class K fires with class B fires because they share relatively the same fuel sources. However, they are very different from each other. Class K fires are caused by combustible liquids and mainly occur in cooking areas during food preparation. These fires are started and fuelled by cooking substances like cooking oils, vegetable fats, greases, and animal fats. Class K fires are dangerous and can cause greater destructions in a short time.",
    extinguishing: "Wet chemicals",
    icon: "ri-restaurant-2-line",
  },
];

export default function TypesOfFiresPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* JSON-LD FAQ */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: fireClasses.map((fc) => ({
              "@type": "Question",
              name: `What is a ${fc.title}?`,
              acceptedAnswer: {
                "@type": "Answer",
                text: `${fc.description} Extinguish with: ${fc.extinguishing}.`,
              },
            })),
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/55ebf370-b69f-400b-bed9-609094da814a_compressed_Depositphotos_211957458_l-2015.webp"
              alt="Types and Classes of Fires Singapore"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Types &amp; Classes of Fires</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Fire Safety Education
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Types and Classes of <span className="text-primary-400">Fires</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Understanding fires is essential to implementing the best measures for fire prevention and safety. When you know the various types and classes of fire, it will be easier to choose the most suitable systems and products for your building.
            </p>
          </div>
        </section>

        {/* Why Know Fire Classes */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Why It Matters
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Why Understanding Fire Classes Matters
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                If you own a residential, commercial or industrial property, you are responsible for the safety of the people and assets therein. Fire emergencies are among some of the biggest threats to safety that most people face in both living and work spaces today.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                There are different classifications of fires primarily based on the sources of fuel or causes. One of the key areas where the knowledge of fire types and classes will come in handy is when buying fire extinguishers. That is because fire extinguishers are rated for putting out different kinds of fires.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed">
                Understanding the various types and classes of fires will enable you to know the potential fire risks on your building. As a result, you will know just the right fire extinguishers to acquire for proper protection whenever they occur.
              </p>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {fireClasses.map((fc) => (
                <div key={fc.id} className="flex flex-col items-center gap-1">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-heading font-bold border ${fc.colorClass}`}>
                    {fc.letter}
                  </div>
                  <p className="text-foreground-500 text-xs text-center leading-tight">{fc.title.split("—")[1].trim()}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Fire Classes Detail */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                The Five Classes
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Main Types and Classes <span className="text-primary-500">of Fires</span>
              </h2>
            </div>
            <div className="flex flex-col gap-6">
              {fireClasses.map((fc) => (
                <div key={fc.id} id={fc.id} className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <div className="flex flex-col md:flex-row md:items-start gap-5">
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-heading font-bold border-2 ${fc.colorClass}`}>
                        {fc.letter}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
                        <a href={`#${fc.id}`}>{fc.title}</a>
                      </h3>
                      <p className="text-foreground-600 text-base leading-relaxed mb-4">{fc.description}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="bg-background-100 rounded-xl p-3 border border-background-200/70">
                          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-1">Fuel Sources</p>
                          <p className="text-foreground-800 text-sm">{fc.fuelSources}</p>
                        </div>
                        <div className="bg-background-100 rounded-xl p-3 border border-background-200/70">
                          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-1">Common Locations</p>
                          <p className="text-foreground-800 text-sm">{fc.commonLocations}</p>
                        </div>
                        <div className="bg-primary-50 rounded-xl p-3 border border-primary-100">
                          <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">How to Extinguish</p>
                          <p className="text-foreground-800 text-sm">{fc.extinguishing}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="bg-primary-500 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-2">
                Choose the Right Fire Extinguisher for Better Fire Safety
              </h2>
              <p className="text-background-100 text-sm md:text-base max-w-xl">
                At Ezzogenics, we provide a wider selection of industry-standard portable fire extinguishers for dealing with all the above types and classes of fires.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link
                to="/products/portable-fire-extinguishers"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                View Extinguishers
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-background-50/40 text-background-50 text-sm font-semibold hover:bg-primary-400 transition-colors whitespace-nowrap"
              >
                Get Expert Advice
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}